import React from 'react';
import { clsx } from 'clsx';

interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
  showTime?: boolean;
}

export function DatePicker({
  label,
  error,
  helperText,
  showTime = false,
  className,
  id,
  ...props
}: DatePickerProps) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        type={showTime ? 'datetime-local' : 'date'}
        className={clsx(
          'block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:[color-scheme:dark]',
          error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700',
          className
        )}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}

interface DateRangePickerProps {
  label?: string;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  error?: string;
  helperText?: string;
  showTime?: boolean;
  className?: string;
}

export function DateRangePicker({
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  error,
  helperText,
  showTime = false,
  className,
}: DateRangePickerProps) {
  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <input
          type={showTime ? 'datetime-local' : 'date'}
          value={startDate}
          onChange={(e) => onStartDateChange?.(e.target.value)}
          className={clsx(
            'block flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1',
            error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700'
          )}
          placeholder="Start date"
        />
        <span className="text-gray-500 dark:text-gray-400">to</span>
        <input
          type={showTime ? 'datetime-local' : 'date'}
          value={endDate}
          onChange={(e) => onEndDateChange?.(e.target.value)}
          min={startDate}
          className={clsx(
            'block flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1',
            error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700'
          )}
          placeholder="End date"
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
